/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import { isValueEmpty, isJsonString, isJsonValueEmpty } from '@awp/helpers/utilsReact';
import { selectAllSensormodels, getjsonObject } from '@stores/sensorEquipmentSlice';

import { defaultSelectStyle } from '@awp/styleConstants';
const txtFontSize = {
  fontSize: '85%',
};

export const ModelMetaFields = ({
  props,
  objName,
  sensor,
  setSensorData,
  fieldErrors,
  setFieldErrors,
  readOnly,
  index,
}) => {
  const dispatch = useDispatch();

  const sensormodelList = useSelector(selectAllSensormodels);
  const jsonObj = getjsonObject(sensor, sensormodelList);
  const objProperties =
    jsonObj && jsonObj.properties && Object.keys(jsonObj.properties).length > 0 ? jsonObj.properties[objName] : null;

  const updateSelectField = (e) => {
    let _sensor = { ...sensor };
    let json = { ...jsonObj };
    const st = sensormodelList.find((opt) => opt.Id == _sensor.ModelId);
    let stJsonObj =
      st && !isValueEmpty(st.SensorAttributeSchema) && isJsonString(st.SensorAttributeSchema)
        ? JSON.parse(st.SensorAttributeSchema)
        : null;
    let currentField = json.properties[objName];
    if (isValueEmpty(currentField)) {
      if (!stJsonObj || !stJsonObj.properties || stJsonObj.properties.length === 0) {
        _sensor.StructuredAttributes = '';
        setSensorData(_sensor);
        props.setFieldValue('sensorMetadata', '');
        props.setFieldTouched('sensorMetadata');
        return;
      }
    }
    if (isValueEmpty(stJsonObj.properties[objName])) {
      json.properties[objName] = null;
    } else {
      currentField = stJsonObj.properties[objName];
      currentField.value = e ? e.value : null;
      json.properties[objName] = currentField;
    }

    _sensor.StructuredAttributes = JSON.stringify(json);
    setSensorData(_sensor);
    props.setFieldValue('sensorMetadata', _sensor.StructuredAttributes);
    props.setFieldTouched('sensorMetadata');
  };

  /**
   * Updates an input field based on the provided event object.
   * @param {Event} e - The event object triggered by an input field.
   */
  const updateInputField = (e) => {
    e.preventDefault();

    // Extract the ID of the target input field from the event.
    let id = e.target.id;

    if (e && e.target) {
      // Remove any error associated with the input field by its ID.
      removeErrByFieldId(id);

      // Create copies of the sensor and jsonObj objects to avoid unintended mutations.
      let _sensor = { ...sensor };
      let json = { ...jsonObj };

      // Find the sensor model in sensormodelList based on _sensor.ModelId.
      const selectedSensorModel = sensormodelList.find((opt) => opt.Id == _sensor.ModelId);

      // Parse the sensor attribute schema if it exists in the sensor model.
      let stJsonObj =
        selectedSensorModel &&
        !isValueEmpty(selectedSensorModel.SensorAttributeSchema) &&
        isJsonString(selectedSensorModel.SensorAttributeSchema)
          ? JSON.parse(selectedSensorModel.SensorAttributeSchema)
          : null;

      // Retrieve the current field within the JSON object.
      let currentField = json.properties[objName];

      // Check if the current field is empty.
      if (isValueEmpty(currentField)) {
        // If no sensor attribute schema or it has no properties, reset the relevant data.
        if (!stJsonObj || !stJsonObj.properties || stJsonObj.properties.length === 0) {
          _sensor.StructuredAttributes = '';
          setSensorData(_sensor);
          props.setFieldValue('sensorMetadata', '');
          props.setFieldTouched('sensorMetadata');
          return;
        }
      }

      // Check if the current field in the schema is empty.
      if (isValueEmpty(stJsonObj.properties[objName])) {
        json.properties[objName] = null;
      } else {
        // Update the currentField to the one from the schema.
        currentField = stJsonObj.properties[objName];

        if (id) {
          // Retrieve the value from the target input field.
          let val = e && e.target ? e.target.value : null;

          // Check if the value should be a numeric value.
          if (!isValueEmpty(val) && (currentField.type + '').toLocaleLowerCase() === 'number') {
            if (isNaN(val) && val !== '-' && isNaN(val + '')) {
              // Display an error message for non-numeric values.
              preErrorCheck(id, 'must be a numeric value');
              setTimeout(() => props.setFieldValue('metaConditions', JSON.stringify(fieldErrors)));
            }
          }

          // Update the value of the current field.
          currentField.value = val;
        }

        // Update the JSON object with the updated currentField.
        json.properties[objName] = currentField;
      }

      // Update the _sensor object with the modified JSON object.
      _sensor.StructuredAttributes = JSON.stringify(json);
      setSensorData(_sensor);

      // Update the form values and touched fields.
      props.setFieldValue('sensorMetadata', _sensor.StructuredAttributes);
      props.setFieldTouched('sensorMetadata');

      // Update the form values for metaConditions with fieldErrors.
      setTimeout(() => props.setFieldValue('metaConditions', JSON.stringify(fieldErrors)));
    }
  };

  const sectionItemsWithSelect = (item, i) => {
    const options = [...item.enum].map((value) => {
      return {
        label: value,
        value: value,
      };
    });

    return (
      item && (
        <Form.Group>
          <Form.Label>
            <b>{item.label}</b>
          </Form.Label>
          <br />
          <div key={`${index}_div_${i}`} style={txtFontSize}>
            {isValueEmpty(objProperties.value) && (
              <Select
                id={`${index}_div_select_${i}`}
                styles={defaultSelectStyle}
                options={options}
                isClearable={true}
                placeholder={objProperties && !isValueEmpty(objProperties.hint) ? objProperties.hint : 'Select...'}
                onChange={updateSelectField}
              />
            )}
            {!isValueEmpty(objProperties.value) && (
              <Select
                id={`${index}_div_select_${i}`}
                styles={defaultSelectStyle}
                options={options}
                isClearable={true}
                value={{
                  value: isValueEmpty(objProperties.value) ? '' : objProperties.value,
                  label: isValueEmpty(objProperties.value) ? '' : objProperties.value,
                }}
                placeholder={'Select...'}
                onChange={updateSelectField}
              />
            )}
          </div>
        </Form.Group>
      )
    );
  };

  /**
   * Performs pre-error-checking by adding an error to the fieldErrors object for a specific field ID.
   * @param {string} fieldId - The ID of the field to which the error belongs.
   * @param {string} err - The error message to be associated with the field ID.
   * @returns {object} - The updated fieldErrors object after adding the error.
   */
  const preErrorCheck = (fieldId, err) => {
    // Create a copy of the fieldErrors object to avoid mutating the original object.
    let _fieldErrors = { ...fieldErrors };

    // Create an array to store the error messages for the field.
    let errorMessages = [];

    // Push the provided error message to the array of error messages.
    errorMessages.push(err);

    // Assign the array of error messages to the specified fieldId in the _fieldErrors object.
    _fieldErrors[fieldId] = errorMessages;

    // Update the fieldErrors state with the modified object.
    setFieldErrors({ ..._fieldErrors });

    // Return the updated _fieldErrors object.
    return _fieldErrors;
  };

  /**
   * Removes an error associated with a specific field ID from the fieldErrors object.
   * @param {string} fieldId - The ID of the field for which to remove the error.
   * @returns {object} - The updated fieldErrors object after removing the error.
   */
  const removeErrByFieldId = (fieldId) => {
    // Create a copy of the fieldErrors object to avoid mutating the original object.
    let _fieldErrors = { ...fieldErrors };

    // If fieldErrors is empty, no need to proceed further.
    if (isJsonValueEmpty(fieldErrors)) {
      return;
    }

    // Check if _fieldErrors is truthy and contains the specified fieldId as a property.
    if (_fieldErrors && _fieldErrors[fieldId]) {
      // Delete the error associated with the specified fieldId.
      delete _fieldErrors[fieldId];
    }

    // Update the fieldErrors state with the modified object.
    setFieldErrors({ ..._fieldErrors });

    // Return the updated _fieldErrors object.
    return _fieldErrors;
  };

  /**
   * Retrieves an error message associated with a specific field ID from the fieldErrors object.
   * @param {string} fieldId - The ID of the field for which to retrieve the error.
   * @returns {string} - The error message for the specified field ID.
   */
  const getError = (fieldId) => {
    // If fieldErrors is empty, return an empty string.
    if (isJsonValueEmpty(fieldErrors)) {
      return '';
    }

    // Create a copy of the fieldErrors object to avoid unintended mutations.
    let err = { ...fieldErrors };

    // Convert fieldId to lowercase for case-insensitive comparison.
    fieldId = (fieldId + '').toLocaleLowerCase();

    // Iterate through each property (field ID) in the err object.
    for (let key in err) {
      // Convert the current property (field ID) to lowercase for comparison.
      let currentFieldId = key.toLocaleLowerCase();

      // Check if the current property (field ID) is not empty and matches the specified fieldId.
      if (!isValueEmpty(key) && currentFieldId === fieldId) {
        // Return the corresponding error message for the matching field ID.
        return err[key];
      }
    }

    // If no matching field ID is found, return an empty string.
    return '';
  };

  const sectionItemsWithInput = (item, i) => {
    let fId = `${index}_input_${i}`;
    return (
      item && (
        <Form.Group>
          <Form.Label>
            <b>{item.label}</b>
          </Form.Label>
          <br />
          <Form.Control
            onChange={updateInputField}
            id={fId}
            type="text"
            value={isValueEmpty(objProperties.value) ? '' : objProperties.value}
            placeholder={objProperties && !isValueEmpty(objProperties.hint) ? objProperties.hint : ''}
          />
          {!isValueEmpty(getError(fId)) && (
            <div className="text-danger mt-1">
              <ul>
                <li key={1}>{getError(fId)}</li>
              </ul>
            </div>
          )}
        </Form.Group>
      )
    );
  };

  return objProperties && !isValueEmpty(objProperties.enum) && objProperties.enum.length > 0
    ? sectionItemsWithSelect(objProperties, index)
    : sectionItemsWithInput(objProperties, index);
};
